import { Dispatch, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { IrisValueObjectsFormatHintEnum } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import { deriveFiltersFromDateFilter } from 'modules/ai_reports/components/DatasourceTypeRenderer'
import { datatableFiltersToJsonLogicFilters } from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import { exportReport } from 'modules/ai_reports/lib/exportReport'
import { ReportSchemaType, ReportType } from 'modules/ai_reports/lib/types'
import { AiReportsResult } from 'modules/ai_reports/lib/useCreateAnalyticsReportActionResolver'
import { useSaveReport } from 'modules/ai_reports/lib/useSaveReport'

import { ReportAction, ReportState } from './useReportCoreState'

function filtersToStrings(filters: AiReportsResult['filters']): {
  snapshotDate?: string
  dateRange?: { from: string; to: string }
} {
  const result: { snapshotDate?: string; dateRange?: { from: string; to: string } } = {}

  if (filters && filters.snapshotDate) {
    result.snapshotDate = filters.snapshotDate
  }

  if (filters && filters.dateRange) {
    result.dateRange = {
      from: filters.dateRange.from,
      to: filters.dateRange.to,
    }
  }

  return result
}

function filtersFromStrings(filters: {
  snapshotDate?: string
  dateRange?: { from: string; to: string }
}) {
  if (filters.snapshotDate) {
    return { snapshotDate: new Date(filters.snapshotDate) }
  }

  if (filters.dateRange) {
    return {
      dateRange: {
        from: new Date(filters.dateRange.from),
        to: new Date(filters.dateRange.to),
      },
    }
  }
}

type UseReportActionsOptions = {
  state: ReportState
  schema: ReportSchemaType
  dispatch: Dispatch<ReportAction>
  agentState: AiReportsResult
  datasourceId: string
  initialReport?: ReportType
  reportName?: string
  onSaveSuccess?: (reportId: string) => void
  setNavigationBlocked: (blocked: boolean) => void
}

const isFormatHint = (hint: unknown): hint is IrisValueObjectsFormatHintEnum => {
  return (
    typeof hint === 'string' &&
    [
      'employee_id',
      'team_id',
      'monetary_amount',
      'monetary_currency',
      'country_code',
      'date',
      'date_time',
      'duration',
      'time',
      'percentage',
    ].includes(hint)
  )
}

const parseFormatHint = (hint: unknown) => {
  if (isFormatHint(hint)) return hint
  throw new Error(`Invalid format hint: ${JSON.stringify(hint)}`)
}

/**
 * Provides save and export actions for reports.
 * Handles loading states, error handling, and navigation after save.
 */
export function useReportActions({
  state,
  schema,
  dispatch,
  agentState,
  datasourceId,
  initialReport,
  reportName,
  onSaveSuccess,
  setNavigationBlocked,
}: UseReportActionsOptions) {
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  // Setup save report mutation
  const { saveReport: saveReportMutation } = useSaveReport({
    onSaveSuccess: (reportId: string) => {
      setNavigationBlocked(false)
      dispatch({ type: 'UNSAVED_CHANGES_CONFIRMED' })
      if (onSaveSuccess) {
        onSaveSuccess(reportId)
      } else if (initialReport?.id !== reportId) {
        setTimeout(() => {
          navigate(navigation.aiReports.reports.reportId.fullPath({ reportId }))
        }, 100)
      }
    },
  })

  const getDerivedFilters = useCallback(() => {
    return deriveFiltersFromDateFilter(state.dateFilter, datasourceId)
  }, [state.dateFilter, datasourceId])

  const getJsonLogicFilters = useCallback(() => {
    if (!state.datatableFilters) return ''
    const jsonLogic = datatableFiltersToJsonLogicFilters(state.datatableFilters, schema)
    return jsonLogic ? JSON.stringify(jsonLogic) : ''
  }, [state.datatableFilters, schema])

  const saveReport = useCallback(
    async ({
      name,
      description,
      saveAsNew,
    }: {
      name: string
      description: string
      saveAsNew?: boolean
    }) => {
      dispatch({ type: 'SAVING_CHANGED', payload: true })
      dispatch({ type: 'ERROR_CHANGED', payload: null })

      try {
        const derivedFilters = getDerivedFilters()
        const jsonLogicFilters = getJsonLogicFilters()

        await saveReportMutation({
          id: saveAsNew ? undefined : initialReport?.id,
          name,
          datasourceId,
          description,
          widgets: [
            {
              name: 'main-table',
              description: state.description,
              query: state.sqlQuery,
              position: {
                x: 0,
                y: 0,
                width: 12,
                height: 6,
              },
            },
          ],
          filters: {
            ...filtersToStrings(derivedFilters),
            jsonLogicFilters,
          },
          columnFormatHints:
            agentState?.sqlAnswer?.column_format_hints.map((hint) => ({
              columnDisplayName: hint.column_alias,
              formatHint: parseFormatHint(hint.column_format),
            })) || [],
          columnOrder: state.columnOrder,
        })

        dispatch({ type: 'UNSAVED_CHANGES_DISMISSED' })
      } catch (error: unknown) {
        if (error instanceof Error) {
          dispatch({ type: 'ERROR_CHANGED', payload: error })
        }
        throw error
      } finally {
        dispatch({ type: 'SAVING_CHANGED', payload: false })
      }
    },
    [
      getDerivedFilters,
      getJsonLogicFilters,
      saveReportMutation,
      initialReport?.id,
      datasourceId,
      state.description,
      state.sqlQuery,
      state.columnOrder,
      agentState?.sqlAnswer?.column_format_hints,
    ]
  )

  const handleExport = useCallback(() => {
    if (!state.sqlQuery) return
    const derivedFilters = getDerivedFilters()
    const jsonLogicFilters = getJsonLogicFilters()
    const columnAliases = Object.fromEntries(
      schema
        .filter((col) => col.displayName && col.displayName !== col.name)
        .map((col) => [col.name, col.displayName ?? col.name])
    )
    exportReport(
      reportName || initialReport?.name || 'AI Report',
      state.sqlQuery,
      company.id,
      datasourceId,
      filtersFromStrings(derivedFilters ?? {}) || {},
      jsonLogicFilters,
      state.columnOrder,
      Object.keys(columnAliases).length > 0 ? columnAliases : undefined
    )
  }, [
    state.sqlQuery,
    getDerivedFilters,
    getJsonLogicFilters,
    reportName,
    initialReport?.name,
    datasourceId,
    company.id,
    state.columnOrder,
    schema,
  ])

  return {
    saveReport,
    handleExport,
  }
}
